/**
 * Large Import Summary — summary-first review UI for large CSV imports.
 *
 * Unlike SummaryView (which renders every row inline), this component NEVER
 * fetches all rows: it shows aggregate counts from `computeImportTotals`, and
 * loads problem rows (invalid/ambiguous) a page at a time via the paginated
 * `/rows` endpoint, only when the user has something to look at.
 */

import { AlertTriangle } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Key } from 'react-aria-components';

import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { PaginationPageDefault } from '@/lib/base/pagination';
import { Tab, TabList, TabPanel, Tabs } from '@/lib/base/tabs/tabs';
import type { CsvImport } from '@/lib/csv-import/api';
import { cx } from '@/utils/cx';

import { useAnalyzedRowsPage } from '../hooks/useAnalyzedRowsPage';
import { useEntitySchemaMap } from '../hooks/useEntitySchemaMap';
import { InvalidAssociationsBanner } from './summary/InvalidAssociationsTable';
import { ProblemRowsTable } from './summary/ProblemRowsTable';
import { SummaryFooter } from './summary/SummaryFooter';
import {
  CATEGORY_CONFIGS,
  COLOR_CLASSES,
  computeImportTotals,
  getMatchColumnsRequiredFilenames,
  getReimportBlockEnabled,
} from './summary/types';

// Re-export for consumers (mirrors SummaryView's convention)
export { SummaryFooter } from './summary/SummaryFooter';

// ============================================================================
// Types
// ============================================================================

interface LargeImportSummaryProps {
  importData: CsvImport;
  orgId: number;
  onApprove: () => void;
  onResolveConflicts: () => void;
  onEditMappings?: () => void;
  isApproving: boolean;
  onReviewCoercions?: () => void;
  coercionCtaLabel?: string;
  isReviewingCoercions?: boolean;
}

type ProblemFilter = 'invalid' | 'ambiguous';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 50;
const PROBLEM_FILTERS: ProblemFilter[] = ['invalid', 'ambiguous'];

// ============================================================================
// Main Component
// ============================================================================

export function LargeImportSummary({
  importData,
  orgId,
  onApprove,
  onResolveConflicts,
  onEditMappings,
  isApproving,
  onReviewCoercions,
  coercionCtaLabel,
  isReviewingCoercions,
}: LargeImportSummaryProps) {
  const totals = useMemo(
    () => computeImportTotals(importData.analysis_result),
    [importData.analysis_result]
  );

  // Gate the Approve button the same way SummaryView does: the backend rejects
  // approval while a file still needs a unique match column (unless the
  // reimport_block_enabled kill switch was off at analysis time). Without
  // wiring these into SummaryFooter, the large-import path would show an enabled
  // "Approve & Import" button that the backend then rejects at submit time.
  const matchColumnsRequiredFilenames = useMemo(
    () => getMatchColumnsRequiredFilenames(importData.analysis_result),
    [importData.analysis_result]
  );

  const reimportBlockEnabled = useMemo(
    () => getReimportBlockEnabled(importData.analysis_result),
    [importData.analysis_result]
  );

  // ------------------------------------------------------------------
  // Property schemas (needed by ProblemRowsTable to render property labels)
  // ------------------------------------------------------------------
  const { schemaMap } = useEntitySchemaMap(orgId, importData.uploaded_files);

  const newPropertyLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const file of importData.uploaded_files) {
      for (const np of file.new_properties ?? []) {
        if (np.property_name && np.label) {
          labels[np.property_name] = np.label;
        }
      }
    }
    return labels;
  }, [importData.uploaded_files]);

  // ------------------------------------------------------------------
  // Problem-rows filter + pagination
  // ------------------------------------------------------------------
  const availableFilters = useMemo(
    () => PROBLEM_FILTERS.filter(f => totals[f] > 0),
    [totals]
  );

  const [selectedFilter, setSelectedFilter] = useState<
    ProblemFilter | undefined
  >(() => availableFilters[0]);
  const activeFilter =
    selectedFilter && availableFilters.includes(selectedFilter)
      ? selectedFilter
      : availableFilters[0];

  const [page, setPage] = useState(1);

  const handleFilterChange = useCallback((key: Key) => {
    setSelectedFilter(key as ProblemFilter);
    setPage(1);
  }, []);

  const offset = (page - 1) * PAGE_SIZE;

  const { rows, count, isLoading, isPlaceholderData, isError, refetch } =
    useAnalyzedRowsPage({
      orgId,
      importId: importData.id,
      action: activeFilter,
      limit: PAGE_SIZE,
      offset,
      enabled: activeFilter !== undefined,
    });

  // `keepPreviousData` keeps the prior page's rows visible while a new
  // filter/offset request is in flight. Those rows belong to the OLD request,
  // so rendering them now would mislabel invalid rows as ambiguous (or show the
  // previous page under the new range). Treat placeholder data as a loading
  // state so the table only ever shows rows that match the active request.
  const isRowsLoading = isLoading || isPlaceholderData;

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  // Clamp the page into range when the underlying result set shrinks (e.g. after
  // coercions fix invalid rows, or the import data refreshes). Without this, a
  // `page` left on (say) 3 after `totalPages` drops to 1 keeps requesting an
  // out-of-range offset, so the table shows a false "No rows on this page" empty
  // state even though the filter still has rows. `handleFilterChange` only
  // resets on manual tab change, not on data changes.
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  // ------------------------------------------------------------------
  // Associations summary (compact, read-only)
  // ------------------------------------------------------------------
  const pendingAssociations = useMemo(
    () => importData.analysis_result?.associations?.pending_associations ?? [],
    [importData.analysis_result]
  );
  const unmatchedAssociations = useMemo(
    () =>
      importData.analysis_result?.associations?.unmatched_associations ?? [],
    [importData.analysis_result]
  );
  const invalidAssociations = useMemo(
    () => importData.analysis_result?.associations?.invalid_associations ?? [],
    [importData.analysis_result]
  );

  const hasNoProblems =
    totals.invalid + totals.ambiguous + totals.conflicts === 0;

  const handleRetry = useCallback(() => refetch(), [refetch]);

  const problemRowsContent = useMemo(() => {
    if (isRowsLoading) {
      return (
        <div className='flex justify-center py-8'>
          <LoadingIndicator size='sm' label='Loading rows…' />
        </div>
      );
    }
    if (isError) {
      return (
        <div
          role='alert'
          className='p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'
        >
          <p className='text-sm text-utility-error-700'>
            We couldn't load the rows.
          </p>
          <Button
            color='secondary'
            size='sm'
            className='mt-3'
            onClick={handleRetry}
          >
            Retry
          </Button>
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          variant='noResults'
          size='sm'
          title='No rows on this page'
          description={`No ${activeFilter} rows on this page`}
        />
      );
    }
    return (
      <div className='space-y-2'>
        <ProblemRowsTable
          orgId={orgId}
          rows={rows}
          category={activeFilter as ProblemFilter}
          schemaMap={schemaMap}
          newPropertyLabels={newPropertyLabels}
          label={activeFilter ? CATEGORY_CONFIGS[activeFilter].title : ''}
        />
        <p className='text-xs text-[var(--color-text-tertiary)]'>
          Showing {offset + 1}–{offset + rows.length} of {count}
        </p>
        {count > PAGE_SIZE && (
          <PaginationPageDefault
            page={page}
            total={totalPages}
            onPageChange={setPage}
          />
        )}
      </div>
    );
  }, [
    isRowsLoading,
    isError,
    rows,
    activeFilter,
    schemaMap,
    newPropertyLabels,
    offset,
    count,
    page,
    totalPages,
    handleRetry,
    orgId,
  ]);

  return (
    <div className='space-y-3 pb-6'>
      {/* Summary count tiles */}
      <div className='flex flex-wrap gap-3'>
        {(['create', 'update', 'invalid', 'ambiguous'] as const).map(cat => {
          const config = CATEGORY_CONFIGS[cat];
          const colors = COLOR_CLASSES[config.color];
          const Icon = config.icon;
          return (
            <div
              key={cat}
              className='flex items-center gap-2 px-3 py-2 border border-[var(--color-border-secondary)] rounded-lg'
            >
              <Icon
                aria-hidden={true}
                className={cx('w-4 h-4 flex-shrink-0', colors.icon)}
              />
              <span className={cx('text-sm font-semibold', colors.count)}>
                {totals[cat]}
              </span>
              <span className='text-xs text-[var(--color-text-tertiary)]'>
                {config.title}
              </span>
            </div>
          );
        })}
        {totals.conflicts > 0 && (
          <div className='flex items-center gap-2 px-3 py-2 border border-utility-warning-200 bg-utility-warning-50 rounded-lg'>
            <AlertTriangle
              aria-hidden={true}
              className='w-4 h-4 flex-shrink-0 text-utility-warning-600'
            />
            <span className='text-sm font-semibold text-utility-warning-700'>
              {totals.conflicts}
            </span>
            <span className='text-xs text-utility-warning-700'>Conflicts</span>
          </div>
        )}
      </div>

      {/* Invalid Association Mappings — always expanded, not an accordion.
          Unlike the compact associations summary below, this is a config
          problem the user should notice immediately, not something to
          discover by scrolling. */}
      <InvalidAssociationsBanner
        invalidAssociations={invalidAssociations}
        importData={importData}
      />

      {/* All-clean state */}
      {hasNoProblems && (
        <EmptyState
          variant='empty'
          size='sm'
          title='No problems found'
          description={`${totals.create + totals.update} record${
            totals.create + totals.update === 1 ? '' : 's'
          } ready to import`}
        />
      )}

      {/* Problem rows — filter + paginated table */}
      {availableFilters.length > 0 && activeFilter && (
        <div className='border border-[var(--color-border-secondary)] rounded-lg p-3'>
          <div className='flex items-center justify-between gap-3 mb-3'>
            <Tabs
              selectedKey={activeFilter}
              onSelectionChange={handleFilterChange}
            >
              <TabList>
                {availableFilters.map(f => (
                  <Tab key={f} id={f}>
                    {CATEGORY_CONFIGS[f].title} ({totals[f]})
                  </Tab>
                ))}
              </TabList>
              <TabPanel id={activeFilter}>{problemRowsContent}</TabPanel>
            </Tabs>
            {activeFilter === 'invalid' &&
              totals.invalid > 0 &&
              onReviewCoercions && (
                <Button
                  color='secondary'
                  size='sm'
                  onClick={onReviewCoercions}
                  isLoading={isReviewingCoercions}
                  isDisabled={isReviewingCoercions}
                  className='shrink-0'
                >
                  {coercionCtaLabel ?? 'Let our agent fix these'}
                </Button>
              )}
          </div>
        </div>
      )}

      {/* Conflict Warning */}
      {totals.conflicts > 0 && (
        <div className='p-3 bg-utility-warning-50 border border-utility-warning-200 rounded-lg'>
          <div className='flex items-start gap-2'>
            <AlertTriangle
              aria-hidden={true}
              className='w-4 h-4 text-utility-warning-600 flex-shrink-0 mt-0.5'
            />
            <div className='flex-1'>
              <p className='text-sm font-medium text-utility-warning-800'>
                {totals.conflicts} Conflict
                {totals.conflicts === 1 ? '' : 's'} Detected
              </p>
              <p className='text-xs text-utility-warning-700 mt-1'>
                Your CSV and existing CRM data overlap in some fields. You can
                resolve conflicts one by one, or choose a bulk rule &mdash; like
                keeping the most complete value for each field.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Associations summary (compact, read-only) */}
      {(pendingAssociations.length > 0 || unmatchedAssociations.length > 0) && (
        <div className='border border-[var(--color-border-secondary)] rounded-lg p-3'>
          <h4 className='text-xs font-medium text-[var(--color-text-primary)] mb-2'>
            Associations
          </h4>
          <div className='space-y-1'>
            {pendingAssociations.length > 0 && (
              <p className='text-xs text-[var(--color-text-secondary)]'>
                {pendingAssociations.length} pending association
                {pendingAssociations.length === 1 ? '' : 's'} detected
              </p>
            )}
            {unmatchedAssociations.length > 0 && (
              <p className='text-xs text-[var(--color-text-secondary)]'>
                {unmatchedAssociations.length} value
                {unmatchedAssociations.length === 1 ? '' : 's'} matched no
                record – those links will be skipped
              </p>
            )}
          </div>
        </div>
      )}

      <SummaryFooter
        totals={totals}
        onApprove={onApprove}
        onResolveConflicts={onResolveConflicts}
        onEditMappings={onEditMappings}
        isApproving={isApproving}
        matchColumnsRequiredFilenames={matchColumnsRequiredFilenames}
        reimportBlockEnabled={reimportBlockEnabled}
        invalidAssociationsCount={invalidAssociations.length}
      />
    </div>
  );
}
